import React, { useState } from "react";
import { useHistory } from "react-router";
import {
  Box,
  Button,
  Container,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import Autocomplete, {
  AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import { SEARCH_GENE_FUSION } from "helpers/apis";
import useAxios from "helpers/axios";
import { colors } from "theme";
import { GeneType } from "types/common";
import { IGeneBasic, IGeneSymbol, IGeneSymbolAlias } from "types/geneSymbol";

interface IAutoCompleteOption extends IGeneBasic {
  searchTerm: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    [theme.breakpoints.down(1024)]: {
      flex: 1,
    },
  },
  searchInputWrapper: {
    background: "white",
    width: 409,
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: 4,
    padding: "7px 11px !important",
    height: 36,
    [theme.breakpoints.down("xl")]: {
      width: 355,
    },
    [theme.breakpoints.down("xl")]: {
      width: 340,
    },
    [theme.breakpoints.down(1024)]: {
      width: "auto",
    },
    transition: "width 0.5s",
    "&.Mui-focused": {
      width: 516,
      [theme.breakpoints.down("xl")]: {
        width: 453,
      },
      [theme.breakpoints.down("xl")]: {
        width: 342,
      },
      [theme.breakpoints.down(1024)]: {
        width: "auto",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  searchInput: {
    padding: "0 !important",
    "&::placeholder": {
      fontSize: 14,
      lineHeight: "20px",
      color: theme.palette.grey[300],
      opacity: 1,
    },
  },
  groupLabel: {
    lineHeight: "20px",
  },
  chipRoot: {
    margin: "0px !important",
    height: "auto",
    position: "relative",
    borderColor: "transparent",
    "&:first-of-type": {
      paddingRight: (props: any) => (props.isFusionSearch ? 10 : 0),
      "& .MuiChip-label::after": {
        content: (props: any) => (props.isFusionSearch ? '"/"' : '""'),
        position: "absolute",
        right: 0,
        bottom: 0,
        top: 0,
      },
    },
  },
  chipLabel: {
    fontSize: 14,
    padding: 0,
    lineHeight: 1.1875,
  },
  option: {
    "&:hover": {
      backgroundColor: colors.grey[500],
    },
  },
}));

const SymbolAutocomplete = () => {
  const history = useHistory();
  const [isFusionSearch, setIsFusionSearch] = useState<boolean>(false);
  const classes = useStyles({ isFusionSearch });
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<IAutoCompleteOption[]>([]);
  const [values, setValues] = useState<IAutoCompleteOption[]>([]); // Selected Genes or Aliases
  const [inputValue, setInputValue] = useState<string>("");
  const { fetchData } = useAxios({
    config: { url: SEARCH_GENE_FUSION },
    callOnInit: false,
    successCallback: ({
      response,
    }: {
      response: {
        data: {
          aliases: IGeneSymbolAlias[];
          genes: IGeneSymbol[];
        };
        message: string;
      };
    }) => {
      if (response?.data) {
        let newOptions: IAutoCompleteOption[] = [];

        if (Array.isArray(response.data?.genes)) {
          newOptions = [
            ...newOptions,
            ...response.data.genes.map((gene: IGeneBasic) => {
              return { ...gene, searchTerm: GeneType.Gene };
            }),
          ];
        }
        if (Array.isArray(response.data?.aliases)) {
          newOptions = [
            ...newOptions,
            ...response.data.aliases.map((alias: IGeneBasic) => {
              return { ...alias, searchTerm: GeneType.Alias };
            }),
          ];
        }

        setOptions(newOptions);
        setOpen(true);
      }
    },
  });

  const onAutocomplete = (
    event: React.ChangeEvent<{}>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason !== "reset") {
      if (newValue === "/") {
        if (values.length === 1) {
          setIsFusionSearch(true);
        }
      } else {
        if (values.length < 1 || (isFusionSearch && values.length < 2)) {
          setInputValue(newValue);

          if (newValue && newValue.length > 1) {
            fetchData({ params: { q: newValue } });
          } else {
            setOpen(false);
          }
        }
      }
    }
    // Set isFusionSearch to False
    if (values.length === 0 && isFusionSearch) {
      setIsFusionSearch(false);
    }
  };

  const onSearch = () => {
    let param = "";

    const genes = values
      .filter(
        (value: IAutoCompleteOption) => value.searchTerm === GeneType.Gene
      )
      .map((value: IAutoCompleteOption) => value.name);
    const aliases = values
      .filter(
        (value: IAutoCompleteOption) => value.searchTerm === GeneType.Alias
      )
      .map((value: IAutoCompleteOption) => value.name);
    if (genes.length) {
      param = `?gene=${genes.join()}`;
    }
    if (aliases.length) {
      if (genes.length) {
        param += `&alias=${aliases.join()}`;
      } else {
        param = `?alias=${aliases.join()}`;
      }
    }

    if (values.length === 0 && inputValue.length > 0) {
      if (inputValue.includes("/")) {
        param = `?fusion=${inputValue}`;
      } else {
        param = `?genealias=${inputValue}`;
      }
    }

    history.push(`/catalog${param}`);
    setInputValue("");
    setValues([]);
    setIsFusionSearch(false);
    setOpen(false);
  };

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        role="search"
        columnGap={1}
        width={1}
      >
        <Autocomplete
          value={values}
          onChange={(event: any, newValue: IAutoCompleteOption[]) => {
            setValues(newValue);
            setInputValue("");
          }}
          onKeyPress={(event: React.KeyboardEvent) => {
            if (event.key === "Enter") {
              onSearch();
            }
          }}
          groupBy={(option: any) => {
            return option.searchTerm === "alias" ? "Gene Alias" : "Gene Symbol";
          }}
          inputValue={inputValue}
          onInputChange={onAutocomplete}
          open={open}
          onClose={() => setOpen(false)}
          multiple
          disableClearable
          ChipProps={{
            onDelete: undefined,
            classes: {
              root: classes.chipRoot,
              label: classes.chipLabel,
            },
          }}
          getOptionLabel={(option) => option.name}
          options={options}
          classes={{
            inputRoot: classes.searchInputWrapper,
            input: classes.searchInput,
            groupLabel: classes.groupLabel,
            root: classes.root,
            option: classes.option,
          }}
          popupIcon={null}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option.name, inputValue);
            const parts = parse(option.name, matches);

            return (
              <li {...props} key={option.id}>
                {parts.map((part, index) => (
                  <Typography
                    component="span"
                    variant="subtitle2"
                    style={{
                      fontWeight: part.highlight ? 600 : 400,
                      letterSpacing: 0.1,
                    }}
                    key={index}
                  >
                    {part.text}
                  </Typography>
                ))}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={values.length > 0 ? "" : "HGNC symbols or fusions"}
              variant="outlined"
            />
          )}
        />

        <BrowseButton
          color="tertiary"
          onClick={onSearch}
          aria-label="Browse catalog or submit search"
        >
          Browse
        </BrowseButton>
      </Box>
    </Container>
  );
};

const BrowseButton = styled(Button)({
  padding: "6px 25px !important",
});

export default SymbolAutocomplete;
